import { useMachine } from '@xstate/react';
import { useCallback } from 'react';

import {
  createForgotPasswordMachine,
  ForgotPasswordState,
} from './forgot-password-machine';
import { logInWithOtp, requestOtp } from './sso';

export function useForgotPassword({
  baseUrl,
  domain,
  on,
}: {
  baseUrl: string;
  domain: string;
  on: Parameters<typeof createForgotPasswordMachine>[0]['on'];
}) {
  const [state, send] = useMachine(() =>
    createForgotPasswordMachine({
      logInWithOtp: ({ email, otp, token }) =>
        logInWithOtp({
          baseUrl,
          email,
          otp,
          token,
        }),
      requestOtp: ({ email }) =>
        requestOtp({
          baseUrl,
          email,
          domain,
        }),
      on,
    }),
  );

  const forgotPasswordState: ForgotPasswordState | null = state.matches('idle')
    ? { value: 'idle', context: state.context }
    : state.matches('requesting otp')
    ? { value: 'requesting otp', context: state.context }
    : state.matches('otp request succeeded')
    ? { value: 'otp request succeeded', context: state.context }
    : state.matches('logging in with otp')
    ? { value: 'logging in with otp', context: state.context }
    : state.matches('log in succeeded')
    ? { value: 'log in succeeded', context: state.context }
    : state.matches('otp request failed')
    ? { value: 'otp request failed', context: state.context }
    : state.matches('log in failed')
    ? { value: 'log in failed', context: state.context }
    : null;

  if (!forgotPasswordState) {
    throw new Error('Unable to derive forgot password state');
  }

  const getOtp = useCallback(
    ({ email }: { email: string }) => {
      send({ type: 'request otp', email });
    },
    [send],
  );

  const logIn = useCallback(
    (
      { otp }: { otp: string },
      callback?: (userId: number, token: string) => void,
    ) => {
      send({ type: 'log in with otp', otp, callback });
    },
    [send],
  );

  const tryAgain = useCallback(() => {
    send({ type: 'request otp again' });
  }, [send]);

  return {
    forgotPasswordState,
    getOtp,
    logIn,
    tryAgain,
  };
}
